import React, { useEffect, useState } from "react";
import { ValidateChemIdTextField } from "./ValidateChemIdTextField";
import { ChemIDSearchField } from "./ChemIDSearch";
import { ValidationStatus } from "./ValidationTextField";
import { Substance } from "./../api/types";

type ChemIDMode = "validate" | "search";

interface BaseChemIDProps {
  mode: ChemIDMode;
  selectedChemIDObject?: Substance | null;
  setSelectedChemIDObject?: (substance: Substance | null) => void;
  hasErrors?: boolean;
  setHasErrors?: (hasErrors: boolean) => void;
  disabled?: boolean;
  label?: string;
  labelCustom?: string;
  allowInactiveChemIDs?: boolean;
  helperText?: string;
  margin?: "none" | "dense" | "normal";
}

interface SearchModeProps {
  fontSize?: number;
  fieldWidth?: string | number;
  returnSubstanceStrucureData?: boolean;
  replaceWithPromotedECCode?: boolean;
}

interface ValidateModeProps {
  placeholder?: string;
  debounceMs?: number;
  minChars?: number;
}

interface ChemIDFieldProps extends BaseChemIDProps, SearchModeProps, ValidateModeProps {}

export const ChemIDField: React.FC<ChemIDFieldProps> = ({
  mode,
  selectedChemIDObject = null,
  setSelectedChemIDObject,
  hasErrors = false,
  setHasErrors,
  disabled = false,
  label,
  labelCustom,
  allowInactiveChemIDs = false,
  helperText,
  margin,
  fontSize,
  fieldWidth,
  returnSubstanceStrucureData,
  replaceWithPromotedECCode = true,
  placeholder = "Enter exact Chem ID",
  debounceMs = 500,
  minChars = 2,
}) => {
  const [chemIdValue, setChemIdValue] = useState<string>("");
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>("idle");

  useEffect(() => {
    if (selectedChemIDObject?.chemID) {
      setChemIdValue(selectedChemIDObject.chemID);
    } else {
      setChemIdValue("");
    }
  }, [selectedChemIDObject]);

  useEffect(() => {
    if (mode === "validate" && setHasErrors) {
      const isError = validationStatus === "invalid" || validationStatus === "error";
      setHasErrors(isError);
    }
  }, [validationStatus, mode, setHasErrors]);

  const handleSubstanceChange = (substance: Substance | null) => {
    setSelectedChemIDObject?.(substance);
  };

  const handleTextChange = (value: string) => {
    setChemIdValue(value);

    if (selectedChemIDObject && value !== selectedChemIDObject.chemID) {
      setSelectedChemIDObject?.(null);
    }
  };

  return mode === "validate" ? (
    <ValidateChemIdTextField
      label={label || labelCustom || "* Chem ID"}
      placeholder={placeholder}
      value={chemIdValue}
      onChange={handleTextChange}
      onStatusChange={setValidationStatus}
      onSubstanceChange={handleSubstanceChange}
      debounceMs={debounceMs}
      minChars={minChars}
      allowInactiveChemIds={allowInactiveChemIDs}
      disabled={disabled}
      inputProps={{ style: { fontSize: fontSize } }}
    />
  ) : (
    <ChemIDSearchField
      fontSize={fontSize}
      fieldWidth={fieldWidth}
      selectedChemIDObject={selectedChemIDObject}
      setSelectedChemIDObject={setSelectedChemIDObject || (() => {})}
      hasErrors={hasErrors}
      setHasErrors={setHasErrors || (() => {})}
      isDisabled={disabled}
      helperText={helperText}
      margin={margin}
      labelCustom={labelCustom || label}
      allowInactiveChemIDs={allowInactiveChemIDs}
      returnSubstanceStrucureData={returnSubstanceStrucureData}
      replaceWithPromotedECCode={replaceWithPromotedECCode}
    />
  );
};
